import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Pencil, Search } from "lucide-react";
import { SchemaField } from "./SchemaField";
import { INTERNI_FIELDS } from "./schemas";
import { CellValue } from "./ResourceTable";

// Iter 105: dipendenti Europe Glass (anagrafica esistente) + campi extra del programma.
// Modifica SOLO i campi extra via PATCH: nome/cognome/IBAN restano in Anagrafica Dipendenti.
export default function InterniTab({ isAdmin, sedi, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/risorse-personale/interni"); setItems(data || []); }
    catch { toast.error("Errore nel caricamento"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => !s || `${it.cognome} ${it.nome} ${it.ruolo || ""} ${it.ubicazione || ""}`.toLowerCase().includes(s));
  }, [items, q]);

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(INTERNI_FIELDS.map((f) => [f.k, editing[f.k] ?? (f.t === "quals" ? [] : "")]));
      await api.patch(`/risorse-personale/interni/${editing.id}`, body);
      toast.success("Salvato");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="risorse-tab-interni">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente…" className="h-10 pl-8" data-testid="risorse-search-interni" />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} / {items.length}</span>
        <span className="ml-auto text-xs text-slate-500">Nome, cognome e IBAN si modificano in <b>Dipendenti</b></span>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-bold">Dipendente</th>
                <th className="px-3 py-2 font-bold">Ruolo</th>
                {INTERNI_FIELDS.filter((f) => f.col).map((f) => <th key={f.k} className="px-3 py-2 font-bold">{f.l}</th>)}
                {isAdmin && <th className="px-3 py-2 w-12" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((it) => (
                <tr key={it.id} className={`hover:bg-blue-50/40 ${it.attivo === false ? "opacity-50" : ""}`} data-testid={`risorse-interno-${it.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">{it.cognome} {it.nome}</td>
                  <td className="px-3 py-2 text-slate-500">{it.ruolo}</td>
                  {INTERNI_FIELDS.filter((f) => f.col).map((f) => <td key={f.k} className="px-3 py-2 max-w-xs truncate"><CellValue f={f} item={it} /></td>)}
                  {isAdmin && (
                    <td className="px-2 py-1.5 text-right">
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-600" onClick={() => setEditing({ ...it, qualifiche: it.qualifiche || [] })} data-testid={`risorse-interno-edit-${it.id}`} title="Modifica"><Pencil className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="risorse-interno-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing?.cognome} {editing?.nome}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INTERNI_FIELDS.map((f) => <SchemaField key={f.k} f={f} value={editing[f.k]} onChange={(v) => setEditing((e) => ({ ...e, [f.k]: v }))} sedi={sedi} />)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="risorse-interno-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
